import { useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight, Mail, MapPin, Phone } from "lucide-react";
import PatientStorySlider from "@/components/testimonials/PatientStorySlider";
import MediaVideoSlider from "@/components/media/MediaVideoSlider";
import HealingJourneyCta from "@/components/HealingJourneyCta";
import WhatsAppIcon from "@/components/icons/WhatsAppIcon";
import SocialIcon, { type SocialNetwork } from "@/components/icons/SocialIcons";
import logoWide from "@/assets/logo-wide.webp";
import accLeaf from "@/assets/accreditation/ayurveda-leaf.png";
import accNabh from "@/assets/accreditation/nabh.png";
import accKerala from "@/assets/accreditation/kerala-tourism.png";
import accAyurDiamond from "@/assets/accreditation/ayur-diamond.png";
import accNcism from "@/assets/accreditation/ncism.png";

/* the sections whose pages carry the patient stories slider above the footer */
const STORY_SECTIONS = /^\/(ayurveda-packages|ayurveda-treatments|holistic-healing)(\/|$)/;
/* where MyVaidyam's videos slide by just above the footer: the home page, every
   treatment, package, holistic healing and centre page, and the regional centre lists */
const VIDEO_SECTIONS = /^\/(?:$|(?:ayurveda-packages|ayurveda-treatments|holistic-healing|top-ayurvedic-centers-in-india)(?:\/|$)|top-\d+-ayurvedic-centers-hospitals-)/;

/* the two numbers the client gave, the first also on WhatsApp */
const PHONES = [
  { label: "+91-9156115572", tel: "+919156115572" },
  { label: "+91-9960133343", tel: "+919960133343" },
];
const EMAIL = "care@myvaidyam.com";

/** a link, or a group (children) that opens under its label on a click */
type FooterLink = { label: string; to?: string; href?: string };

/* MyVaidyam's offices, shown when "Office Address" is opened; each opens in
   Google Maps. The client has two more to add: one entry each. */
const OFFICES: { name: string; lines: string[]; map: string }[] = [
  {
    name: "Navi Mumbai",
    lines: ["Sea Breeze Towers, Tower-5, F-1203", "Palm Beach Road, Nerul", "Navi Mumbai, Maharashtra 400705, India"],
    map: "Sea Breeze Towers, Palm Beach Road, Nerul, Navi Mumbai, Maharashtra 400705",
  },
];
const mapLink = (q: string) => "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(q);

/* every link is one of the site's own pages; the city guides are static
   pages outside the app, hence href. The columns are the client's: Quick
   Links (which took in the old Knowledge column's guides) and In Demand. */
const COLUMNS: { title: string; links: FooterLink[]; office?: boolean }[] = [
  {
    title: "Quick Links",
    links: [
      { label: "Country", to: "/country-guide" },
      { label: "Your City", href: "/geo/" },
      { label: "AYUSH Visa", to: "/ayush-visa" },
      { label: "Center Videos", to: "/center-videos" },
      { label: "Media", to: "/media" },
      { label: "Knowledge", to: "/knowledge-center" },
    ],
  },
  {
    // the programmes and treatment groups most asked about: arthritis, joint
    // pain and spine open the Pain Management treatments (arthritis, knee and
    // back pain, sciatica, spondylosis, disc bulge), neurological rehab the
    // Neurological Disorders ones (stroke, Parkinson's, neuropathy…)
    title: "In Demand",
    links: [
      { label: "Weight Loss", to: "/ayurveda-packages/ayurvedic-weight-loss-program-in-india" },
      { label: "Stress Management", to: "/ayurveda-packages/stress-management-ayurveda-retreat-in-india" },
      { label: "Arthritis, Joint Pain & Spine", to: "/ayurveda-treatments?category=pain-management" },
      { label: "Rejuvenation & Detox", to: "/ayurveda-packages/panchakarma-detox" },
      { label: "Neurological Rehab", to: "/ayurveda-treatments?category=neurological-disorders" },
      { label: "Anti-Aging", to: "/ayurveda-packages/anti-aging-ayurveda-program-in-india" },
    ],
  },
  {
    // Mission, About Us (The Expertise Behind MyVaidyam), Why MyVaidyam and
    // Center Tie-ups (Partner With MyVaidyam) each open their own page; FAQs
    // opens the FAQ Knowledge Centre. Team has no page yet (and no Author /
    // Reviewer entries under it any more, as the client asked).
    title: "Profile",
    links: [
      { label: "Mission", to: "/our-mission" },
      { label: "About Us", to: "/about-us" },
      { label: "Why MyVaidyam", to: "/why-myvaidyam" },
      { label: "Team" },
      { label: "Center Tie-ups", to: "/center-tie-ups" },
      { label: "FAQs", to: "/frequently-asked-questions" },
    ],
  },
  {
    // the office address first
    title: "Services",
    office: true,
    links: [
      { label: "Refer to Us", to: "/refer-to-us" },
      { label: "Contact Us", to: "/contact-us" },
    ],
  },
];

/* The band under the columns — Follow Us, the policy pages, Accreditation —
   after the client's reference. These are MyVaidyam's own accounts, pages and
   accreditations. Entries still to come from the client (a link or logo not
   yet given) show as dimmed placeholders, on the live site as in development,
   as the client asked, so the footer reads the same everywhere. */
const SOCIAL: { name: string; network: SocialNetwork; href: string }[] = [
  { name: "Facebook", network: "facebook", href: "https://www.facebook.com/people/MyVaidyam-Wellnesss-India/61595155291248/" },
  { name: "X", network: "x", href: "https://x.com/myvaidyam" },
  { name: "Pinterest", network: "pinterest", href: "https://www.pinterest.com/myvaidyamindia/" },
  { name: "Instagram", network: "instagram", href: "https://www.instagram.com/myvaidyam/" },
  { name: "Threads", network: "threads", href: "https://www.threads.com/@myvaidyam" },
  { name: "YouTube", network: "youtube", href: "https://www.youtube.com/@MyVaidyam" },
];
const POLICIES: { label: string; to: string }[] = [
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Editorial Policy", to: "/editorial-policy" },
  { label: "Disclaimer", to: "/disclaimer" },
];
/* the accreditation logos the client gave, in their order (NABH, NCISM and
   Kerala Tourism from the bodies' own sites, Ayur Diamond and the leaf mark
   from the client's images) */
const ACCREDITATIONS: { name: string; logo: string }[] = [
  { name: "Leaf logo", logo: accLeaf },
  { name: "NABH Accredited", logo: accNabh },
  { name: "Kerala Tourism – God's Own Country", logo: accKerala },
  { name: "Ayur Diamond – Department of Tourism, Govt. of Kerala", logo: accAyurDiamond },
  { name: "NCISM – National Commission for Indian System of Medicine", logo: accNcism },
];
const PLACEHOLDERS = true;
const PLACEHOLDER_TILES = 6;

/* a link with its chevron; one with no page yet shows dimmed, not as a link */
function footerLink(l: FooterLink) {
  const inner = (
    <>
      <ChevronRight className="h-3 w-3 shrink-0 text-[#9A5F3A] transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
      {l.label}
    </>
  );
  if (l.to) return <Link to={l.to} className={linkClass}>{inner}</Link>;
  if (l.href) return <a href={l.href} className={linkClass}>{inner}</a>;
  // aria-disabled: an entry still to come, dimmed on purpose (not text to read)
  return <span title="page to come" aria-disabled="true" className={`${linkClass} opacity-50`}>{inner}</span>;
}

const linkClass = "group inline-flex items-center gap-1.5 text-[13.5px] text-[#2C4E5A]/90 transition-colors hover:text-[#2C4E5A]";
const iconRound =
  "flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2C4E5A]/40";

/**
 * The site footer, on every page: the brand block (logo, what MyVaidyam
 * does, WhatsApp / phones / email) beside four columns of links, then the
 * copyright line, after the client's reference layout, on the light sage
 * (#D6E1DA) the client chose, with the brand teal for the text.
 * On package, treatment and holistic healing pages the patient stories
 * slider sits above it; on those, the centre pages and the home page,
 * MyVaidyam's videos (media/MediaVideoSlider) sit directly above it.
 */
/** cta: end the page with the "Ready to Begin Your Healing Journey?" call to action */
/** beforeCta: shown between the patient stories and the call to action */
const Footer = ({ cta = false, beforeCta }: { cta?: boolean; beforeCta?: ReactNode }) => {
  const { pathname } = useLocation();
  const [officesOpen, setOfficesOpen] = useState(false);
  // on a phone each link column is a row that opens on a tap; these are open
  const [openCols, setOpenCols] = useState<Record<string, boolean>>({});
  const toggleCol = (title: string) => setOpenCols((o) => ({ ...o, [title]: !o[title] }));
  return (
    <>
      {STORY_SECTIONS.test(pathname) && <PatientStorySlider />}
      {beforeCta}
      {cta && <HealingJourneyCta />}
      {VIDEO_SECTIONS.test(pathname) && <MediaVideoSlider />}
      <footer className="border-t border-[#2C4E5A]/10 bg-[#D6E1DA] text-[#2C4E5A]">
        <div className="container mx-auto max-w-7xl px-4 py-8 md:py-10">
          {/* phone: the brand block, then each link column as a row that opens
              on a tap; tablet: two columns, all open; desktop: one row */}
          <div className="grid grid-cols-2 gap-x-6 md:gap-y-8 lg:grid-cols-[1.35fr_1fr_1.2fr_1fr_1.15fr] lg:gap-6">
            {/* brand and contact */}
            <div className="col-span-2 mb-5 md:mb-0 lg:col-span-1">
              <Link to="/" className="inline-block" aria-label="MyVaidyam home">
                <img src={logoWide} alt="MyVaidyam" className="aspect-[433/100] h-11 w-auto object-cover" loading="lazy" decoding="async" />
              </Link>
              <p className="mt-3.5 max-w-xs text-[13.5px] leading-[1.7] text-[#2C4E5A]/90">
                20+ Years of Trusted Medical Travel Expertise. Your Gateway to Wellness in India.
              </p>
              <div className="mt-4 flex items-center gap-2.5">
                <a href="https://wa.me/919156115572" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp MyVaidyam" className={`${iconRound} bg-[#2C4E5A] text-white`}>
                  <WhatsAppIcon className="h-4 w-4" />
                </a>
                <a href={`tel:${PHONES[0].tel}`} aria-label="Call MyVaidyam" className={`${iconRound} border border-[#2C4E5A]/30 text-[#2C4E5A]`}>
                  <Phone className="h-3.5 w-3.5" />
                </a>
                <span className="flex flex-col">
                  {PHONES.map((ph) => (
                    <a key={ph.tel} href={`tel:${ph.tel}`} className="inline-block py-1 text-[13.5px] font-semibold text-[#2C4E5A] hover:underline">
                      {ph.label}
                    </a>
                  ))}
                </span>
              </div>
              <div className="mt-2.5 flex items-center gap-2.5">
                <a href={`mailto:${EMAIL}`} aria-label="Email MyVaidyam" className={`${iconRound} border border-[#2C4E5A]/30 text-[#2C4E5A]`}>
                  <Mail className="h-3.5 w-3.5" />
                </a>
                <a href={`mailto:${EMAIL}`} className="break-all text-[13.5px] font-semibold text-[#2C4E5A] hover:underline">
                  {EMAIL}
                </a>
              </div>
            </div>

            {COLUMNS.map((col) => {
              const open = !!openCols[col.title];
              const id = `footer-col-${col.title.toLowerCase().replace(/\s+/g, "-")}`;
              return (
                <nav key={col.title} aria-label={col.title} className="col-span-2 border-t border-[#2C4E5A]/15 last:border-b md:col-span-1 md:border-0 md:last:border-b-0">
                  {/* phone: the title is the row's button */}
                  <button
                    type="button"
                    onClick={() => toggleCol(col.title)}
                    aria-expanded={open}
                    aria-controls={id}
                    className="flex w-full items-center justify-between gap-3 py-3.5 text-left text-[14.5px] font-bold uppercase tracking-[0.1em] text-[#2C4E5A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2C4E5A]/40 md:hidden"
                  >
                    {col.title}
                    <ChevronDown className={`h-4 w-4 shrink-0 text-[#9A5F3A] transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
                  </button>
                  <h2 className="hidden text-[14.5px] font-bold uppercase tracking-[0.1em] text-[#2C4E5A] md:block">{col.title}</h2>
                  <span className="mt-2.5 hidden h-0.5 w-7 rounded-full bg-[#C48257] md:block" aria-hidden="true" />
                  {/* closed on a phone until its row is tapped; always open from a tablet up */}
                  <div id={id} className={`${open ? "block" : "hidden"} pb-4 md:block md:pb-0`}>
                    {col.office && (
                      <div className="md:mt-4">
                        {/* the addresses open and close on a click */}
                        <button
                          type="button"
                          onClick={() => setOfficesOpen((o) => !o)}
                          aria-expanded={officesOpen}
                          aria-controls="footer-offices"
                          className="flex w-full items-center gap-1.5 text-left text-[13.5px] font-semibold text-[#2C4E5A] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2C4E5A]/40"
                        >
                          <MapPin className="h-3.5 w-3.5 shrink-0 text-[#9A5F3A]" aria-hidden="true" />
                          Office Address
                          <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${officesOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                        </button>
                        {officesOpen && (
                          <ul id="footer-offices" className="mt-2.5 space-y-2.5 pl-5">
                            {OFFICES.map((o) => (
                              <li key={o.name}>
                                <a href={mapLink(o.map)} target="_blank" rel="noopener noreferrer" className="block text-[13.5px] leading-relaxed text-[#2C4E5A]/90 hover:text-[#2C4E5A]">
                                  {OFFICES.length > 1 && <span className="block font-semibold text-[#2C4E5A]">{o.name}</span>}
                                  {o.lines.map((line) => (
                                    <span key={line} className="block">{line}</span>
                                  ))}
                                </a>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                    <ul className={`space-y-2.5 text-[13.5px] md:mt-4 ${col.office ? "mt-3" : ""}`}>
                      {col.links.filter((l) => l.to || l.href || PLACEHOLDERS).map((l) => (
                        <li key={l.label}>{footerLink(l)}</li>
                      ))}
                    </ul>
                  </div>
                </nav>
              );
            })}
          </div>
        </div>

        <FooterBand />

        <div className="border-t border-[#2C4E5A]/10">
          {/* on a phone the floating Browse / Quote buttons sit over the foot of the page: the lines end above them */}
          <div className="container mx-auto flex max-w-7xl flex-col gap-2 px-4 pb-24 pt-4 text-center text-xs text-[#2C4E5A]/90 md:flex-row md:items-center md:justify-between md:pb-4 md:text-left md:text-[12.5px]">
            <p>&copy; {new Date().getFullYear()} MyVaidyam. All rights reserved.</p>
            {/* two lines on a phone: the size follows the screen width (10.5px on a
                320px phone, 12px from 400px), then 13px from a tablet up */}
            <p className="text-[length:clamp(10.5px,3vw,12px)] md:text-[12.5px]">
              <strong className="font-bold text-[#9A3D0E]">Disclaimer:</strong> Information on this website is for support purposes only and does not constitute medical advice.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

/* a heading with the copper bar under it, as on the link columns */
const BandHeading = ({ children, right = false }: { children: string; right?: boolean }) => (
  <>
    <h2 className="text-[14.5px] font-bold uppercase tracking-[0.1em] text-[#2C4E5A]">{children}</h2>
    <span className={`mt-2.5 block h-0.5 w-7 rounded-full bg-[#C48257] ${right ? "md:ml-auto" : ""}`} aria-hidden="true" />
  </>
);

const FooterBand = () => {
  const social = SOCIAL.filter((s) => s.href || PLACEHOLDERS);
  const policies = POLICIES.filter((p) => p.to || PLACEHOLDERS);
  const tiles: ({ name: string; logo: string } | null)[] = ACCREDITATIONS.length
    ? ACCREDITATIONS
    : PLACEHOLDERS ? Array.from({ length: PLACEHOLDER_TILES }, () => null) : [];
  if (!social.length && !policies.length && !tiles.length) return null;

  return (
    <div className="border-t border-[#2C4E5A]/10">
      {/* phone: one under another; tablet: Follow Us and Accreditation side by
          side, the policies under them; from xl the reference's one row, the
          policies in the middle of the page, level with the icons and logos.
          The policies are centred at every width. */}
      <div className="container mx-auto grid max-w-7xl gap-6 px-4 py-6 md:grid-cols-2 xl:grid-cols-[1fr_auto_1fr] xl:items-end">
        {/* an empty side column is kept only on xl, where it holds the
            policies in the middle; narrower it would leave a blank row */}
        <div className={social.length ? "" : "hidden xl:block"}>
          {social.length > 0 && (
            <>
              <BandHeading>Follow Us</BandHeading>
              <div className="mt-4 flex flex-wrap items-center gap-2.5 xl:h-[56px]">
                {/* icons only, not links, as the client asked (the addresses stay in SOCIAL) */}
                {social.map((s) => (
                  <span key={s.name} title={s.name} aria-label={`MyVaidyam on ${s.name}`} role="img"
                    className="flex h-[34px] w-[34px] items-center justify-center rounded-full border border-[#2C4E5A]/30 text-[#2C4E5A]">
                    <SocialIcon network={s.network} />
                  </span>
                ))}
              </div>
            </>
          )}
        </div>

        {policies.length > 0 && (
          // on phones a line under Follow Us sets the policies apart, as the columns above are
          <nav aria-label="Policies" className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 border-t border-[#2C4E5A]/15 pt-5 text-center text-[13px] font-bold text-[#2C4E5A] md:order-last md:col-span-2 md:border-0 md:pt-0 xl:order-none xl:col-span-1 xl:h-[56px]">
            {policies.map((p, i) => (
              <span key={p.label} className="inline-flex items-center gap-2">
                {i > 0 && <span className="h-1 w-1 rounded-full bg-[#2C4E5A]/40" aria-hidden="true" />}
                {p.to ? (
                  <Link to={p.to} className="hover:underline">{p.label}</Link>
                ) : (
                  <span title="page to come" aria-disabled="true" className="opacity-50">{p.label}</span>
                )}
              </span>
            ))}
          </nav>
        )}

        <div className={tiles.length ? "md:text-right" : "hidden xl:block"}>
          {tiles.length > 0 && (
            <>
              <BandHeading right>Accreditation</BandHeading>
              {/* one row at every width: the tiles are square and, where the
                  row is too narrow for their full size, shrink to share it */}
              <div className="mt-4 flex gap-2 sm:gap-2.5 md:justify-end">
                {tiles.map((t, i) =>
                  t ? (
                    <div key={t.name} className="relative aspect-square min-w-0 max-w-[52px] flex-1 rounded-lg bg-white shadow-sm">
                      <img src={t.logo} alt={t.name} title={t.name} className="absolute inset-0 h-full w-full object-contain p-1" loading="lazy" />
                    </div>
                  ) : (
                    <div key={i} title="accreditation logo to come"
                      className="flex aspect-square min-w-0 max-w-16 flex-1 items-center justify-center rounded-xl border border-dashed border-[#2C4E5A]/30 bg-white/60 text-[10px] font-semibold uppercase tracking-wide text-[#2C4E5A]/40">
                      Logo
                    </div>
                  )
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Footer;
